// Вставка и ввод формул:
//  - HTML из Word с формулами (OMML в <!--[if gte msEquation 12]>) -> формулы редактора (строчные и выключные,
//    номер «(N)», таблица-раскладка «формула | (N)», экспликация «где …» из следующих абзацев);
//  - MathML (в т. ч. с веб-страниц: <math> с аннотацией TeX), OMML-текст, LaTeX ($…$, $$…$$, \[…\], \(…\),
//    \begin{equation}) -> формулы;
//  - ввод: «$x^2$» -> строчная формула, «$$» + пробел в пустом абзаце -> выключная, Enter на выделенной формуле -
//    правка; копирование формул - с MathML (Word вставляет их как формулы).
import { Extension, InputRule } from '@tiptap/core';
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state';
import type { RegistryApi, Ctx, PasteData } from '../../core/registry';
import { insertBlocks } from '../../core/editor/insert';
import { canon } from './latex';
import { anyToLatex, detectFormat } from './detect';
import { convertWordEquations, hasWordEquations, mathmlToLatex } from './omml';
import { mathUI } from './state';
import { toMathML } from './ml';

const fix = (latex: string) => canon(latex).latex;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** <math> в HTML (веб-страницы) -> узлы формул редактора. */
export function convertHtmlMathML(html: string): { html: string; count: number } {
  let count = 0;
  const out = html.replace(/<math[\s>][\s\S]*?<\/math>/gi, (m) => {
    const r = mathmlToLatex(m);
    if (!r.latex) return m;
    count++;
    const latex = fix(r.latex);
    return r.display
      ? `<div data-type="formula" data-latex="${esc(latex)}" data-numbered="true"></div>`
      : `<span data-type="math-inline" data-latex="${esc(latex)}">${esc(latex)}</span>`;
  });
  return { html: out, count };
}

/** Есть ли в HTML что-то кроме формул (текст, картинки, таблицы). */
function onlyFormulas(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('[data-type="formula"],[data-type="math-inline"],style,script,meta,title,o\\:p').forEach((e) => e.remove());
  return !(doc.body.textContent || '').replace(/[\s ]+/g, '') && !doc.body.querySelector('img,table');
}

/** Вставить узлы формул из преобразованного HTML: выключные - блоками, строчные - в текст. */
function insertFormulaHtml(ctx: Ctx, html: string) {
  const ed = ctx.editor;
  if (!ed) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nodes = Array.from(doc.querySelectorAll('[data-type="formula"],[data-type="math-inline"]'));
  const blocks = nodes.filter((n) => n.getAttribute('data-type') === 'formula');
  const $from = ed.state.selection.$from;
  const emptyPara = $from.parent.type.name === 'paragraph' && $from.parent.content.size === 0;
  if (blocks.length || (emptyPara && nodes.length === 1 && /\\(frac|sum|int|begin|sqrt|lim|prod)/.test(nodes[0].getAttribute('data-latex') || ''))) {
    const list = nodes.map((n) => ({
      type: 'formula',
      attrs: {
        latex: n.getAttribute('data-latex') || '',
        numbered: n.getAttribute('data-numbered') !== 'false',
        where: safeJson(n.getAttribute('data-where')),
      },
    }));
    insertBlocks(ed, list);
  } else {
    ed.chain().focus().insertContent(nodes.map((n) => ({ type: 'mathInline', attrs: { latex: n.getAttribute('data-latex') || '' } }))).run();
  }
  ctx.toast(nodes.length > 1 ? `Вставлено формул: ${nodes.length}` : 'Формула вставлена - щёлкните, чтобы изменить', 'ok', 2500);
}

function safeJson(s: string | null) { try { return s ? JSON.parse(s) : []; } catch { return []; } }

/** Вставить формулу (LaTeX) у курсора: выключную - блоком, строчную - в текст. */
export function insertLatex(ctx: Ctx, latex: string, display: boolean, numbered = true) {
  const ed = ctx.editor;
  if (!ed) return;
  const l = fix(latex);
  if (display) insertBlocks(ed, { type: 'formula', attrs: { latex: l, numbered, where: [] } });
  else ed.chain().focus().insertContent({ type: 'mathInline', attrs: { latex: l } }).run();
}

const DISPLAY_RE = /^\s*(?:\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|(\\begin\{(equation|align|gather|multline)\*?\}[\s\S]+\\end\{\4\*?\}))\s*$/;
const INLINE_RE = /^\s*(?:\$([^$\n]+)\$|\\\(([\s\S]+?)\\\))\s*$/;

export function setupPaste(r: RegistryApi) {
  // 1) Word: формулы OMML. Только формулы (скопирована формула) - вставляем сами; вместе с текстом - это вставка
  //    фрагмента Word (paste_import: структура, «где», номера); для стандартного разбора - преобразование HTML.
  r.paste.add({ id: 'math.wordEquations', priority: 105,
    match: (d) => !!d.html && hasWordEquations(d.html),
    handle: (d: PasteData, c) => {
      const r1 = convertWordEquations(d.html, fix);
      if (!r1.count) return false;
      if (onlyFormulas(r1.html)) { insertFormulaHtml(c, r1.html); return true; }
      return false;                     // формулы вместе с текстом - разбор структуры Word (paste_import)
    } });
  r.htmlTransforms.add({ id: 'math.wordEquations', order: 5, transform: (html) => {
    let h = html;
    if (hasWordEquations(h)) h = convertWordEquations(h, fix).html;
    if (/<math[\s>]/i.test(h)) h = convertHtmlMathML(h).html;
    return h;
  } });

  // 2) MathML (текстом или HTML только из <math>) и OMML-текст
  r.paste.add({ id: 'math.mathml', priority: 106,
    match: (d) => !d.files.length && (/^\s*<(mml:)?math[\s>]/i.test(d.text) || /^\s*<m:oMath/i.test(d.text)
      || (/<math[\s>]/i.test(d.html) && onlyFormulas(convertHtmlMathML(d.html).html))),
    handle: (d, c) => {
      if (/^\s*<(mml:)?math[\s>]|^\s*<m:oMath/i.test(d.text)) {
        const r2 = anyToLatex(d.text);
        if (!r2.latex) return false;
        insertLatex(c, r2.latex, r2.display !== false && !inTextParagraph(c), r2.numbered !== false);
        return true;
      }
      insertFormulaHtml(c, convertHtmlMathML(d.html).html);
      return true;
    } });

  // 3) LaTeX: $$…$$, \[…\], \begin{equation} - выключная; $…$, \(…\) - строчная
  r.paste.add({ id: 'math.latex', priority: 60,
    match: (d) => !d.files.length && (DISPLAY_RE.test(d.text) || INLINE_RE.test(d.text)) && !/<table/i.test(d.html) && !hasWordEquations(d.html),
    handle: (d, c) => {
      const m = d.text.match(DISPLAY_RE);
      if (m) { insertLatex(c, (m[1] || m[2] || m[3]).trim(), true); return true; }
      const i = d.text.match(INLINE_RE)!;
      insertLatex(c, (i[1] || i[2]).trim(), false);
      return true;
    } });

  // 4) в выделенную формулу: вставка формулы любого формата заменяет её
  r.paste.add({ id: 'math.replaceSelected', priority: 110,
    match: (d, c) => {
      const sel = c.editor?.state.selection;
      return !!sel && sel instanceof NodeSelection && ['formula', 'mathInline'].includes(sel.node.type.name) && !d.files.length
        && !!(d.text.trim() || hasWordEquations(d.html)) && detectFormat(hasWordEquations(d.html) ? d.html : d.text) !== 'text';
    },
    handle: (d, c) => {
      const ed = c.editor!;
      const sel = ed.state.selection as NodeSelection;
      const r3 = anyToLatex(hasWordEquations(d.html) ? d.html : d.text);
      if (!r3.latex) return false;
      ed.view.dispatch(ed.state.tr.setNodeMarkup(sel.from, undefined, { ...sel.node.attrs, latex: r3.latex }));
      c.toast('Формула заменена', 'ok', 1500);
      return true;
    } });
}

function inTextParagraph(c: Ctx): boolean {
  const $f = c.editor?.state.selection.$from;
  return !!$f && $f.parent.type.name === 'paragraph' && $f.parent.content.size > 0;
}

// ------------------------------------------------------------------------------------------------ ввод и копирование
const INLINE_INPUT = /(^|[^$\\\w])\$([^$\s](?:[^$]*[^$\s\\])?)\$$/;

export const MathInputExtension = Extension.create({
  name: 'stoMathInput',
  addInputRules() {
    return [
      // «$x^2$» -> строчная формула
      new InputRule({
        find: INLINE_INPUT,
        handler: ({ state, range, match }) => {
          const latex = fix(match[2]);
          if (!latex) return null;
          const from = range.from + match[1].length;
          state.tr.replaceWith(from, range.to, state.schema.nodes.mathInline.create({ latex }));
        },
      }),
      // «$$ » в пустом абзаце -> выключная формула (сразу правка)
      new InputRule({
        find: /^\$\$\s$/,
        handler: ({ state, range }) => {
          const $f = state.doc.resolve(range.from);
          if ($f.parent.type.name !== 'paragraph') return null;
          const start = $f.before();
          state.tr.replaceWith(start, $f.after(), state.schema.nodes.formula.create({ latex: '', numbered: true, where: [] }));
          mathUI.requestEdit(start);
        },
      }),
    ];
  },
  addKeyboardShortcuts() {
    return {
      // Enter на выделенной формуле - правка на месте (как F2)
      Enter: () => {
        const sel = this.editor.state.selection;
        if (sel instanceof NodeSelection && ['formula', 'mathInline'].includes(sel.node.type.name)) {
          mathUI.requestEdit(sel.from);
          return true;
        }
        return false;
      },
      F2: () => {
        const sel = this.editor.state.selection;
        if (sel instanceof NodeSelection && ['formula', 'mathInline'].includes(sel.node.type.name)) { mathUI.requestEdit(sel.from); return true; }
        return false;
      },
    };
  },
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('stoMathCopy'),
      props: {
        handleDOMEvents: {
          copy: (view, e) => copyWithMath(view, e as ClipboardEvent, false),
          cut: (view, e) => copyWithMath(view, e as ClipboardEvent, true),
        },
      },
    })];
  },
});

/** Копирование фрагмента с формулами: в HTML к каждой формуле добавляется MathML (Word вставит формулу Word). */
function copyWithMath(view: any, e: ClipboardEvent, cut: boolean): boolean {
  const sel = view.state.selection;
  if (sel.empty || !e.clipboardData) return false;
  let has = false;
  view.state.doc.nodesBetween(sel.from, sel.to, (n: any) => { if (n.type.name === 'formula' || n.type.name === 'mathInline') has = true; return !has; });
  if (!has) return false;
  try {
    const { dom, text } = view.serializeForClipboard(sel.content());
    dom.querySelectorAll('[data-type="formula"],[data-type="math-inline"]').forEach((el: Element) => {
      const latex = el.getAttribute('data-latex') || '';
      if (!latex) return;
      el.innerHTML = toMathML(latex, el.getAttribute('data-type') === 'formula');
    });
    e.clipboardData.clearData();
    e.clipboardData.setData('text/html', dom.innerHTML);
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
    if (cut) view.dispatch(view.state.tr.deleteSelection().scrollIntoView().setMeta('uiEvent', 'cut'));
    return true;
  } catch {
    return false;
  }
}
